import { List } from 'immutable'
import React, { ReactElement, useEffect, useState } from 'react'
import { Link } from 'react-router'
import { usePrevious } from 'react-use'

import { addAlertSignature } from '../../actions/alerts'
import paths from '../../constants/paths'
import AsynchronousTask from '../../model/asynchronousTask'
import Company from '../../model/company'
import Employee from '../../model/employee'
import SalaryType from '../../model/salaryType'
import { AgreementReducer } from '../../reducers/agreements'
import { AsynchronousTaskReducer } from '../../reducers/asynchronousTasks'
import { SupportAccessReducer } from '../../reducers/supportAccess'
import { regularComponentDidUpdate } from '../../utils/component-utils'
import { formatDateTime, getDate } from '../../utils/date-utils'
import { formatError } from '../../utils/error-utils'
import { t } from '../../utils/translation-utils'
import Alert from '../elements/alert'
import Button from '../elements/button'
import Card from '../elements/card'
import Col from '../elements/grid/col'
import Modal from '../elements/modal'
import Title from '../elements/Title'
import FeatureLock from '../widgets/FeatureLock'
import LoadingOverlay from '../widgets/LoadingOverlay'
import ImportExcelEmployeesModal from './ImportExcelEmployeesModal'
import RequestDeletionModal from './RequestDeletionModal'
import RequestImportModal from './RequestImportModal'

type Props = {
  agreements: AgreementReducer
  company: Company
  asynchronousTasks: AsynchronousTaskReducer
  salaryTypes: List<SalaryType>
  employees: List<Employee>
  supportAccess: SupportAccessReducer

  addAlert: addAlertSignature
  grantSupportAccess: () => void
  revokeSupportAccess: () => void
  loadSupportAccess: () => void
  startExcelEmployeesData: (id: string) => Promise<AsynchronousTask | void>
  storeStagedImportData: (id: string) => Promise<AsynchronousTask | void>
  getAsynchronousTasks: (id: string) => void

  isPremiumCustomer?: boolean
  supportPhoneNumber?: string
}

export default function SupportTab(props: Props): ReactElement | null {
  const [modalKey, setModalKey] = useState<number>(1)
  const [showImport, setShowImport] = useState<boolean>(false)
  const [showRequestDeletion, setShowRequestDeletion] = useState<boolean>(false)
  const [showRequestImport, setShowRequestImport] = useState<boolean>(false)
  const [showPhoneNumber, setShowPhoneNumber] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const { supportAccess, loadSupportAccess } = props

  useEffect(() => {
    if (!supportAccess.loading && !supportAccess.loaded) {
      loadSupportAccess()
    }
  }, [supportAccess, loadSupportAccess])

  const asynchronousTaskLoading = props.asynchronousTasks.loading
  const asynchronousTaskLoaded = props.asynchronousTasks.loaded
  const getAsynchronousTasks = props.getAsynchronousTasks
  const company = props.company

  useEffect(() => {
    if (!asynchronousTaskLoading && !asynchronousTaskLoaded) {
      getAsynchronousTasks(company.id)
    }
  }, [asynchronousTaskLoaded, asynchronousTaskLoading, getAsynchronousTasks, company])

  const supportAccessSaving = props.supportAccess.saving
  const supportAccessError = props.supportAccess.error
  const wasSupportAccessSaving = usePrevious(supportAccessSaving)

  const addAlert = props.addAlert

  useEffect(() => {
    if (wasSupportAccessSaving && !supportAccessSaving) {
      if (!supportAccessError) {
        addAlert('success', t('support_tab.alert.success', { name: company.name }), { timeout: 5 })
      }
    }
  }, [wasSupportAccessSaving, supportAccessSaving, supportAccessError, addAlert, company])

  useEffect(() => {
    regularComponentDidUpdate(supportAccessError, error, setError)
  }, [supportAccessError, error])

  const setImportVisibility = (showImport: boolean): void => {
    setModalKey(modalKey + 1)
    setShowImport(showImport)
  }

  const setRequestDeletionVisibility = (show: boolean): void => {
    setModalKey(modalKey + 1)
    setShowRequestDeletion(show)
  }

  const setRequestImportVisibility = (show: boolean): void => {
    setModalKey(modalKey + 1)
    setShowRequestImport(show)
  }

  const setPhoneNumberVisibility = (show: boolean) => {
    setShowPhoneNumber(show)
  }

  const handleCallUsClick = (e: React.MouseEvent) => {
    e.preventDefault()

    if (props.isPremiumCustomer) {
      setPhoneNumberVisibility(true)
    }
  }

  if (!props.supportAccess.loaded) {
    return (
      <div className="support-tab__loading-container">
        <LoadingOverlay />
      </div>
    )
  }

  const hasSupportAccess = props.supportAccess.expiresAt

  return (
    <div className="support-tab">
      <Card>
        <Title>{t('support_tab.title')}</Title>
        {/* Adminstrative functions section */}
        <div className="support-tab__content">
          {error && <Alert message={formatError(error)} type="error" showIcon />}

          <Card className="support-tab__section-card">
            <div className="support-tab__actions">
              <Title>{t('support_tab.administrative_functions')}</Title>
              <Col span={24} className="support-tab__help-item">
                <div className="support-tab__access-info">
                  {t('support_tab.access.title')}:
                  <br />
                  <strong>
                    {hasSupportAccess
                      ? t('support_tab.access.until', {
                          time: formatDateTime(props.supportAccess.expiresAt || getDate()),
                        })
                      : t('support_tab.access.false')}
                  </strong>
                </div>
                <Button
                  onClick={hasSupportAccess ? props.revokeSupportAccess : props.grantSupportAccess}
                  size="large"
                  type="secondary"
                  className="support-tab__action-button"
                >
                  {hasSupportAccess ? t('support_tab.access.remove') : t('support_tab.access.provide')}
                </Button>
              </Col>

              {props.supportAccess.saving && <LoadingOverlay />}

              <Col span={24} className="support-tab__help-item">
                <FeatureLock featureType={'Import Export'} description={t('support_tab.request_import.button.lock')}>
                  <Button
                    size="large"
                    type="secondary"
                    className="support-tab__action-button "
                    style={{ maxWidth: '300px !important' }}
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault()
                      setRequestImportVisibility(true)
                    }}
                  >
                    {t('support_tab.request_import.button')}
                  </Button>
                </FeatureLock>
              </Col>

              <Col span={24} className="support-tab__help-item">
                <FeatureLock featureType={'Import Export'} description={t('support_tab.employee_import.button.lock')}>
                  <Button
                    type="secondary"
                    size="large"
                    className="gtm-import-employees-excel support-tab__action-button "
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault()
                      setImportVisibility(true)
                    }}
                  >
                    {t('support_tab.employee_import.button')}
                  </Button>
                </FeatureLock>
              </Col>

              <Col span={24} className="support-tab__help-item">
                <Button
                  danger
                  className="gtm-request-deletion "
                  size="large"
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault()
                    setRequestDeletionVisibility(true)
                  }}
                >
                  {t('support_tab.request_deletion.button')}
                </Button>
              </Col>

              <div className="support-tab__terms">
                <p className="support-tab__terms-header support-tab__pro-tip-text">
                  <strong>{t('support_tab.terms_and_agreement')}</strong>
                </p>
                <div className="support-tab__terms-buttons">
                  <Link to={'/' + paths.TERMS}>
                    <Button>{t('support_tab.header.terms')}</Button>
                  </Link>
                  <Link to={'/' + paths.DATA_PROVIDER_TERMS}>
                    <Button>{t('support_tab.header.data_provider_terms')}</Button>
                  </Link>
                  <Link to={'/' + paths.PRIVACY_POLICY}>
                    <Button>{t('support_tab.header.privacy_policy')}</Button>
                  </Link>
                  <Link to={'/' + paths.COOKIE_POLICY}>
                    <Button>{t('support_tab.header.cookie_policy')}</Button>
                  </Link>
                </div>
              </div>
            </div>
          </Card>

          {/* Need help section */}
          <Card className="support-tab__section-card">
            <div className="support-tab__help-card">
              <Title>{t('support_tab.need_help')}</Title>
              <div className="support-tab__help-content">
                <Col className="support-tab__help-item">
                  <Link target="_blank" to={'https://help.salary.dk/da/articles/11738192-faq'}>
                    <Button type="secondary" size="large" className="support-tab__help-button">
                      {t('support_tab.faq')}
                    </Button>
                  </Link>
                </Col>

                <Col className="support-tab__help-item">
                  <Link target="_blank" to={'https://help.salary.dk/da/'}>
                    <Button type="secondary" size="large" className="support-tab__help-button">
                      {t('support_tab.help_articles')}
                    </Button>
                  </Link>
                </Col>

                {/* Commented out until actual advisor url is available*/}
                {/* <Col className="support-tab__help-item">
                  <Link target="_blank" to={'https://salary.dk/find-raadgiver/'}>
                    <Button type="secondary" size="large" className="support-tab__help-button">
                      {t('support_tab.find_advisor')}
                    </Button>
                  </Link>
                </Col> */}

                <Col className="support-tab__help-item">
                  {props.isPremiumCustomer ? (
                    <Button
                      type="secondary"
                      size="large"
                      className="support-tab__help-button"
                      onClick={handleCallUsClick}
                    >
                      {t('support_tab.call_us')}
                    </Button>
                  ) : (
                    <FeatureLock
                      featurePackage="Premium"
                      description={t('support_tab.phone_support.feature_lock')}
                      featureType="Phone Support"
                    >
                      <Button type="secondary" size="large" className="support-tab__help-button">
                        {t('support_tab.call_us')}
                      </Button>
                    </FeatureLock>
                  )}
                  <p className="support-tab__help-description support-tab__help-description--phone">
                    {t('support_tab.phone_support.description')}
                  </p>
                </Col>

                <Col className="support-tab__help-item support-tab__help-item--last">
                  <div className="support-tab__pro-tip">
                    <p className="support-tab__pro-tip-text">
                      <strong>{t('support_tab.pro_tip.label')}</strong>{' '}
                    </p>
                    <p className="support-tab__pro-tip-text">{t('support_tab.pro_tip.text')}</p>
                  </div>
                </Col>
              </div>
            </div>
          </Card>
        </div>
      </Card>

      <Modal
        key={`import-${modalKey}`}
        visible={showImport}
        onOk={() => setImportVisibility(false)}
        onCancel={() => setImportVisibility(false)}
        width={980}
        footer={null}
      >
        <ImportExcelEmployeesModal
          visible={showImport}
          asynchronousTasks={props.asynchronousTasks}
          salaryTypes={props.salaryTypes}
          employees={props.employees}
          startExcelEmployeesData={props.startExcelEmployeesData}
          storeStagedImportData={props.storeStagedImportData}
          closeModal={() => setImportVisibility(false)}
        />
      </Modal>

      <Modal
        key={`request-deletion-${modalKey}`}
        visible={showRequestDeletion}
        onOk={() => setRequestDeletionVisibility(false)}
        onCancel={() => setRequestDeletionVisibility(false)}
        width={600}
        footer={null}
      >
        <RequestDeletionModal
          company={props.company}
          addAlert={props.addAlert}
          closeModal={() => setRequestDeletionVisibility(false)}
        />
      </Modal>

      <Modal
        key={`request-import-${modalKey}`}
        visible={showRequestImport}
        onOk={() => setRequestImportVisibility(false)}
        onCancel={() => setRequestImportVisibility(false)}
        width={600}
        footer={null}
      >
        <RequestImportModal
          company={props.company}
          addAlert={props.addAlert}
          closeModal={() => setRequestImportVisibility(false)}
        />
      </Modal>

      <Modal visible={showPhoneNumber} onCancel={() => setPhoneNumberVisibility(false)} width={480} footer={null}>
        <div className="support-tab__phone-modal">
          <p className="support-tab__phone-modal-description">{t('support_tab.phone_modal.description')}</p>

          <div className="support-tab__phone-number-display">
            <div className="support-tab__phone-number">{props.supportPhoneNumber}</div>
          </div>

          <div className="support-tab__phone-modal-hours">
            <small>{t('support_tab.phone_modal.hours')}</small>
          </div>
        </div>
      </Modal>
    </div>
  )
}
